import { useGetNotificationsCountQuery } from '@sketch/gql-types'
import { useDebounceValue } from '@sketch/utils'

import { useInboxSearchContext } from './InboxSearchContext'
import { buildSearchString } from './searchUtils'

const SEARCH_DEBOUNCE_MS = 300

/**
 * Total count of UNREAD notifications matching the active inbox filters
 * (structured options + free-text search), independent of pagination.
 *
 * `hasActiveFilters` is false when the inbox is in its default, unfiltered
 * state — the empty preview already surfaces the global unread count there,
 * so the per-filter line is only relevant once something narrows the list.
 * The query is skipped in that case.
 *
 * Search is debounced to mirror the list query in `useInboxNotifications`,
 * keeping the count in step with what the list shows rather than firing on
 * every keystroke.
 */
export const useFilteredUnreadNotificationsCount = () => {
  const { search, options, isOptionsEmpty } = useInboxSearchContext()
  const debouncedSearch = useDebounceValue(search, SEARCH_DEBOUNCE_MS)

  const hasActiveFilters = !isOptionsEmpty || debouncedSearch.trim().length > 0
  const searchString = buildSearchString({ search: debouncedSearch, options })

  // `cache-and-network` keeps the count reconciled on (re)mount; marking
  // notifications read/unread nudges this exact cache field live from
  // `useSetNotificationState`.
  const { data } = useGetNotificationsCountQuery({
    variables: {
      filter: {
        search: searchString || undefined,
        states: ['UNREAD'],
      },
    },
    skip: !hasActiveFilters,
    fetchPolicy: 'cache-and-network',
  })

  return {
    hasActiveFilters,
    count: data?.me?.notifications?.meta?.totalCount ?? 0,
  }
}
